import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Bell } from 'lucide-react';
import { PersonaSwitcher } from './PersonaSwitcher';
import { AvatarMenu } from './AvatarMenu';
import { NotificationsPanel } from './NotificationsPanel';
interface NavItem {
  label: string;
  icon: any;
  path: string;
  badge?: string;
}
interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  personaLabel?: string;
  personaColor?: string;
  navItems: NavItem[];
  userInitials: string;
}
export function DashboardLayout({
  children,
  title,
  personaLabel,
  personaColor = 'bg-gray-200 text-gray-600 border-gray-300',
  navItems,
  userInitials
}: DashboardLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const location = useLocation();
  const avatarRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLButtonElement>(null);
  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (avatarRef.current && !avatarRef.current.contains(event.target as Node)) {
        setIsAvatarMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setIsNotifOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div className="min-h-screen bg-[#f5f5f7] font-sans text-[#1f2933]" data-id="element-180">
      {/* Navbar */}
      <header className="bg-white border-b border-gray-300 py-4 px-4 md:px-12 flex justify-between items-center sticky top-0 z-30" data-id="element-181">
        <div className="flex items-center gap-3" data-id="element-182">
          <button className="md:hidden p-2 -ml-2 text-gray-600" onClick={() => setIsSidebarOpen(!isSidebarOpen)} data-id="element-183">
            {isSidebarOpen ? <X className="w-6 h-6" data-id="element-184" /> : <Menu className="w-6 h-6" data-id="element-185" />}
          </button>

          <div className="flex items-center gap-2" data-id="element-186">
            <Link to="/" className="w-8 h-8 bg-[#1f2933] rounded-sm hidden sm:block" data-id="element-187"></Link>
            <Link to="/" className="font-bold text-xl tracking-tight" data-id="element-188">
              Citiplots.com
            </Link>
            {personaLabel && <span className={`hidden sm:inline-block px-2 py-0.5 text-xs font-bold border rounded ml-2 ${personaColor}`} data-id="element-189">
                {personaLabel}
              </span>}
          </div>
        </div>

        <div className="flex items-center gap-4 md:gap-6" data-id="element-190">
          <PersonaSwitcher data-id="element-191" />

          <div className="relative" data-id="element-192">
            <button ref={notifRef} onClick={() => setIsNotifOpen(!isNotifOpen)} className="relative hidden sm:block p-1 hover:bg-gray-100 rounded-full" data-id="element-193">
              <Bell className="w-5 h-5 text-gray-600" data-id="element-194" />
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" data-id="element-195"></span>
            </button>
            <NotificationsPanel isOpen={isNotifOpen} onClose={() => setIsNotifOpen(false)} data-id="element-196" />
          </div>

          <div className="relative" ref={avatarRef} data-id="element-197">
            <button onClick={() => setIsAvatarMenuOpen(!isAvatarMenuOpen)} className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs border cursor-pointer hover:opacity-80 transition-opacity ${personaColor}`} data-id="element-198">
              {userInitials}
            </button>
            <AvatarMenu isOpen={isAvatarMenuOpen} onClose={() => setIsAvatarMenuOpen(false)} userInitials={userInitials} personaColor={personaColor} data-id="element-199" />
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-65px)] relative" data-id="element-200">
        {/* Mobile Sidebar Overlay */}
        {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-201" />}

        {/* Sidebar */}
        <aside className={`
          fixed md:sticky top-[65px] left-0 h-[calc(100vh-65px)] w-64 bg-white border-r border-gray-300 
          transform transition-transform duration-200 ease-in-out z-20 overflow-y-auto
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `} data-id="element-202">
          <div className="p-6 flex flex-col h-full" data-id="element-203">
            <h2 className="text-xs font-mono font-bold text-gray-500 uppercase mb-4" data-id="element-204">
              {title}
            </h2>
            <nav className="space-y-1 flex-1" data-id="element-205">
              {navItems.map(item => {
              const isActive = location.pathname === item.path;
              return <Link key={item.path} to={item.path} onClick={() => setIsSidebarOpen(false)} className={`flex items-center gap-3 px-3 py-2 font-medium rounded-sm transition-colors ${isActive ? 'bg-gray-100 text-[#1f2933]' : 'text-gray-600 hover:bg-gray-50 hover:text-[#1f2933]'}`} data-id="element-206">
                    <item.icon className={`w-4 h-4 ${isActive ? 'text-[#1f2933]' : 'text-gray-500'}`} data-id="element-207" />
                    <span data-id="element-208">{item.label}</span>
                    {item.badge && <span className="ml-auto bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-id="element-209">
                        {item.badge}
                      </span>}
                  </Link>;
            })}
            </nav>

            <div className="pt-6 border-t border-gray-200 mt-auto" data-id="element-210">
              <button className="flex items-center gap-3 text-gray-500 hover:text-red-600 text-sm font-medium w-full" data-id="element-211">
                <LogOut className="w-4 h-4" data-id="element-212" /> Sign Out
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 lg:p-12 w-full overflow-x-hidden" data-id="element-213">
          <div className="max-w-7xl mx-auto" data-id="element-214">{children}</div>
        </main>
      </div>
    </div>;
}